import React from "react";
import { useDndMonitor } from "@dnd-kit/core";
import { Vector2, Vector3 } from "three";
import { Strecke2 } from "../../arrangement/grundrissFunctions";
import { dndEventPosition } from "./AufbauMenuTeil";
import * as Maths from "oc-commons/Maths";
import * as Maths2 from "oc-threejs/Maths2";
import * as Maths3 from "oc-threejs/Maths3";
import { SVG } from "./SVG";
import { punkteZuLinie } from "./Zaunlinien";
import { MAuswahlwert } from "../../../metamodell";
import { ZElement } from "../../arrangement/ZElement";
import { GFlügel, TorFlügel } from "./Part";
import { Segment } from "../../arrangement/Segment";
import classnames from "classnames";
import * as Utils from "../../commons/Utils";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { CoordinateSystem } from "./CoordinateSystem";

export interface TorState {
  auswahlwert: MAuswahlwert;
  segment: Segment;
  point: Vector2;
  mousePosition: Vector2;
  dragOffsetSegment: Segment;
  dragOffsetX: number;
  tor: ZElement;
}

interface DragInfo {
  segment: Segment;
  point: Vector2;
  x: number;
  mousePosition: Vector2;
}

export const eventToGrundrissPosition = (cs: CoordinateSystem, event) =>
  Maths3.v3To2xz(dndEventPosition(cs, event) as Vector3);

export const mouseToPointOnSegment = (cs: CoordinateSystem, mousePosition: Vector2): DragInfo => {
  const kantenInfos = APP.arrangement.segmente.map((segment) => {
    const punkte = segment.segmentKante.punkte;
    const point = Maths2.closestPointToLineSegment(mousePosition, punkte);
    const distance = point.distanceTo(mousePosition);
    return { segment, point, distance };
  });
  const infos = _.minBy(kantenInfos, ({ distance }) => distance);
  if (!infos) {
    return null;
  }
  const { segment, point } = infos;
  const x = segment.getXOfWorldPosition(Maths3.v2To3xz(point));
  return { segment, point, x, mousePosition };
};

function addDragOffsetX(
  dragInfo: DragInfo,
  {
    dragOffsetX,
    dragOffsetSegment,
    tor,
  }: { dragOffsetSegment: Segment; dragOffsetX: number; tor: ZElement }
): DragInfo {
  if (dragInfo == null) {
    return null;
  }
  let { segment, point, x, mousePosition } = dragInfo;
  if (dragOffsetSegment === segment) {
    const xo = x + dragOffsetX;
    x = Maths.clamp(tor.breite / 2, xo, segment.breite - tor.breite / 2);
    point = Maths3.v3To2xz(segment.getWorldPositionOfX(x));
  }
  return { segment, point, x, mousePosition };
}

export const onDragStartTor = (
  cs: CoordinateSystem,
  mousePosition: Vector2,
  tor: ZElement,
  torStateRef: React.MutableRefObject<TorState>,
  forceUpdate: () => void
) => {
  const info = mouseToPointOnSegment(cs, mousePosition);
  if (!info) {
    return;
  }
  const auswahlwert = APP.richMetadata.findAuswahlwert(
    tor.isSchiebetor
      ? "Grundriss_Schiebetor"
      : tor.isEinzeltor
        ? "Grundriss_Einzeltor"
        : "Grundriss_Doppeltor"
  );
  const [x0, x1] = getTorMinMaxX(tor);
  const torCenterX = (x0 + x1) / 2;
  const dragOffsetX = torCenterX - info.x;
  const dragOffsetSegment = info.segment;
  const torState = {
    ...addDragOffsetX(info, { dragOffsetSegment, dragOffsetX, tor }),
    auswahlwert,
    tor,
    dragOffsetX,
    dragOffsetSegment,
  };
  torStateRef.current = torState;
  forceUpdate();
};

export const onDragMoveTor = (
  cs: CoordinateSystem,
  mousePosition: Vector2,
  torStateRef: React.MutableRefObject<TorState>,
  forceUpdate: () => void
) => {
  const info = addDragOffsetX(mouseToPointOnSegment(cs, mousePosition), torStateRef.current);
  if (!info) {
    return;
  }
  torStateRef.current = { ...torStateRef.current, ...info };
  forceUpdate();
};

export function insertTor(segment: Segment, side: number, x: number, tor: ZElement) {
  const verteilungsstrategieAuswahlwertBefore = segment.verteilungsstrategieAuswahlwert;
  // segment.parts = [];
  segment
    .filterPartsBetweenPositionsX(x - tor.breite / 2, x + tor.breite / 2)
    .forEach((p) => p.uiRemove());
  segment.addPart(tor);
  tor.breite;
  segment.setX(tor, side, x);
  const [x0, x1] = [0, 1].map((side) => segment.getX(tor, side));
  const [überstandLinks, überstandRechts] = [Math.min(x0, 0), Math.max(x1 - segment.breite, 0)];
  const delta = überstandLinks + überstandRechts;
  tor.position.x -= delta;
  tor.snap = 0;
  segment.verteilungsstrategieAuswahlwert = verteilungsstrategieAuswahlwertBefore;
}

export const onDragEndTor = (
  cs: CoordinateSystem,
  mousePosition: Vector2,
  torStateRef: React.MutableRefObject<TorState>,
  forceUpdate: () => void
) => {
  const info = addDragOffsetX(mouseToPointOnSegment(cs, mousePosition), torStateRef.current);
  if (!info) {
    return;
  }
  const { segment, x } = info;
  const tor = torStateRef.current.tor;
  insertTor(segment, 0.5, x, tor);
  torStateRef.current = null;
  forceUpdate();
  APP.dirty();
  APP.logUserAction({ action: "dropTor", type: tor.typname });
};

export function TorDragging({
  cs,
  torStateRef,
  forceUpdate,
}: {
  cs: CoordinateSystem;
  torStateRef: React.MutableRefObject<TorState>;
  forceUpdate: () => void;
}) {
  useDndMonitor({
    onDragStart(event) {
      const auswahlwert = event?.active?.data?.current as MAuswahlwert;
      if (auswahlwert?.Auswahl == null) {
        return;
      }
      const form =
        auswahlwert.Key == "Grundriss_Schiebetor"
          ? "Schiebetor"
          : auswahlwert.Key == "Grundriss_Einzeltor"
            ? "Einzeltor"
            : "Doppeltor";
      const agt = APP.arrangement;
      const json = {
        form,
        füllungAuswahlwert: agt.füllungAuswahlwert?.Key,
        abschlussleisteAuswahlwert: agt.abschlussleisteAuswahlwert?.Key,
      };
      const tor = agt.createElementFromJson(json);
      APP.arrangement.ghostPool.withPool(() => {
        tor.breite = tor.meta.Mass.Standard.x;
        tor.updateInner();
      });
      const mousePosition = eventToGrundrissPosition(cs, event);
      const torState = torStateRef.current ?? {
        dragOffsetSegment: null,
        dragOffsetX: 0,
      };
      const info = addDragOffsetX(mouseToPointOnSegment(cs, mousePosition), { ...torState, tor });
      torStateRef.current = { auswahlwert, tor, ...torState, ...info };
      forceUpdate();
    },
    onDragMove(event) {
      if (torStateRef.current?.tor == null) return;
      const auswahlwert = event?.active?.data?.current;
      if (auswahlwert?.Auswahl == null) {
        return;
      }
      const mousePosition = eventToGrundrissPosition(cs, event);
      onDragMoveTor(cs, mousePosition, torStateRef, forceUpdate);
    },
    onDragEnd(event) {
      const tor = torStateRef.current?.tor;
      if (tor == null) return;
      if (segment?.segmentKante?.punkte == null) {
        Dialogs.showToast(I18N("bitte zeichnen Sie zuerst eine Zaunlinie"));
        return null;
      }
      const auswahlwert = event?.active?.data?.current;
      if (auswahlwert?.Auswahl == null) {
        return;
      }
      const mousePosition = eventToGrundrissPosition(cs, event);
      onDragEndTor(cs, mousePosition, torStateRef, forceUpdate);
    },
  });
  if (torStateRef.current?.tor == null) {
    return null;
  }
  const toScreen = (p) => cs.pointToScreen(Maths3.v2To3xz(p));

  const { point, segment, tor } = torStateRef.current;
  if (segment?.segmentKante?.punkte == null) {
    return null;
  }
  const strecke = segment.segmentKante.punkte;
  const kantenBreite = strecke[0].distanceTo(strecke[1]);
  const direction = segment.segmentKante.grundrissKante.direction;
  const px = strecke[0].distanceTo(point);
  const halbBreite = tor.breite / 2;
  const middle = Maths.cutBetween(halbBreite, px, kantenBreite - halbBreite);
  const xs = [middle - halbBreite, middle + halbBreite];
  const xToV2 = (x) => strecke[0].clone().addScaledVector(direction, x);
  const torPoints = xs.map(xToV2);
  const isSchiebetor = tor.isSchiebetor;
  const gflügel: GFlügel[] = isSchiebetor
    ? []
    : tor.flügel.map((flügel) => {
    const box = flügel.getBox();
    return {
      xs: [box.min.x, box.max.x],
      isAufhängungAnLinkerTorseite: flügel.isAufhängungAnLinkerTorseite,
      isÖffnungInSegmentNormale: flügel.isÖffnungInSegmentNormale,
      localToWorld: (v3: Vector3) => {
        const v = v3.clone();
        v.x += flügel.position.x + xs[0];
        v.applyMatrix4(segment.matrixWorld);
        return v;
      },
    };
  });

  const strokeWidth = cs.lengthToScreen(0.2);

  const rulerOffset = 10;
  const normalPx = cs.sizeToScreen(segment.normal).negate();
  const addNormalPx = (v) => v.clone().add(normalPx);
  const eckpunktePx = [0, ...xs, kantenBreite].map(xToV2).map(toScreen).map(addNormalPx);

  const rulers = [
    [0, 2],
    [2, 4],
  ]
    .map(([a, b]) => eckpunktePx.slice(a, b))
    .map((points) => ({
      points,
      middle: Maths2.middle(points),
      distance: cs.lengthToGrundriss(points[0].distanceTo(points[1])),
    }));

  return (
    <div className="rulers grid-fullscreen no-pointer-events-tree fullsize">
      <SVG cs={cs} className="">
        {torStateRef.current && (
          <>
            {rulers.map(({ points }, index) => (
              <line
                key={index}
                {...punkteZuLinie(points)}
                className="svg-line-ruler-2"
                strokeWidth={strokeWidth}
              ></line>
            ))}
            <line
              {...punkteZuLinie(torPoints.map(toScreen))}
              className="svg-door-drag"
              strokeWidth={strokeWidth}
            ></line>
            <TorFlügel cs={cs} gflügel={gflügel} className="svg-door-drag" />
          </>
        )}
      </SVG>
      {rulers.map(({ distance, middle }, index) => (
        <div
          key={index}
          className="ruler-text"
          style={{ left: middle.x + "px", top: middle.y + "px" }}
        >
          <div className={classnames("ruler-text-inner")}>{Utils.formatM(distance, 2)}</div>
        </div>
      ))}
    </div>
  );
}

export function findTorAt(startV2: Vector2, cs: CoordinateSystem) {
  if (!cs) return null;
  const minDistance = cs.lengthToGrundriss(20);
  const tor = APP.arrangement.getTore().find((tor) => {
    const strecke = getTorStrecke(tor);
    const distance = Maths2.distanceToLineSegment(startV2, strecke);
    return distance < minDistance;
  });
  return tor;
}

function getTorStrecke(tor: ZElement): Strecke2 {
  const segment = tor.segment;
  return getTorMinMaxX(tor).map((x) => Maths3.v3To2xz(segment.getWorldPositionOfX(x))) as Strecke2;
}

function getTorMinMaxX(tor: ZElement): [number, number] {
  const segment = tor.segment;
  return [0, 1].map((side) => segment.getX(tor, side)) as [number, number];
}
